import { Injectable, inject, signal, computed } from '@angular/core';
import { IOTObject } from '../models/object.model';
import { Notification } from '../models/classes/object';
import { Location } from '../models/classes/location';
import { Sensor } from '../models/classes/sensor';
import { Asset } from '../models/classes/asset';
import { LocationFilter } from '../models/classes/location.filter';
import { BuildingAreaSettings } from '../models/buildingareasettings.model';
import { MeasurementSystemSettings } from '../models/settings.model';
import { AssetDetail } from '../models/classes/asset';
import { IFlatMarker } from '../models/marker.model';
import { AssetsApi } from '../api/assets.api';
import { GLOBAL_CONSTANT } from '../constants/global.constant';
import { LP_UI_CONFIG } from '../core/tokens';
import {
  ParseSensorsFromRawSensorTelemetry,
  ParseHealthFromRawSensorTelemetry as ParseHealthEM,
  ParseSteamTrapFromRawSensorTelemetry,
  ParseTemperatureFromRawSensorTelemetry,
  CurrentTelemetry as EMCurrentTelemetry,
  Sensor as EMSensor,
} from '../models/classes/sensor';
import { TelemetryHistory } from '../models/classes/telemetry-history';
import { HistoryItem } from '../models/classes/history';

@Injectable({ providedIn: 'root' })
export class AssetsStore {
  private readonly cfg = inject(LP_UI_CONFIG);
  private assetsApi = inject(AssetsApi);

  // ✅ Private mutable signals
  private _loading = signal(false);
  private _navigation = signal<IOTObject[]>([]);
  private _regions = signal<Location[]>([]);
  private _sensors = signal<Sensor[]>([]);
  private _searchTerm = signal(GLOBAL_CONSTANT.EMPTY_STRING);
  private _filter = signal<LocationFilter>(new LocationFilter());
  private _selectionPreset = signal<Location[]>([]);
  private _storageOptions = signal<unknown | null>(null);
  private _lazyReload = signal(false);
  private _nextObjectId = signal(0);
  private _navInProgress = signal(false);
  private _navLoadPromise: Promise<void> | null = null;
  private _sensorsInProgress = signal(false);

  // NanoPrecise data
  private _nanoPrecisePosts = signal<unknown | null>(null);
  //private _nanoPreciseToken = signal<unknown | null>(null);

  // ✅ Public readonly signals
  readonly loading = this._loading.asReadonly();
  readonly navigation = this._navigation.asReadonly();
  readonly regions = this._regions.asReadonly();
  readonly sensors = this._sensors.asReadonly();
  readonly searchTerm = this._searchTerm.asReadonly();
  readonly filter = this._filter.asReadonly();
  readonly selectionPreset = this._selectionPreset.asReadonly();
  readonly storageOptions = this._storageOptions.asReadonly();

  //Equipment monitor
  private _currentAssetDetail = signal<any | null>(null);
  private _assetDetailCache = new Map<number, { data: any; timestamp: number }>();
  private readonly ASSET_CACHE_TIME = 100000;
  private _notifications = signal<any[]>([]);
  private _telemetryData = signal<any | null>(null);
  private _mapboxKeys = signal<{
    satelliteView: string;
    mapView: string;
    geoCodingToken: string;
  } | null>(null);
  private _sensorsWithTelemetry = signal<{
    sensors: Sensor[];
    health: any[];
    steamtrap: any[];
    temperature: any;
  } | null>(null);

  readonly currentAssetDetail = this._currentAssetDetail.asReadonly();
  readonly notifications = this._notifications.asReadonly();
  readonly telemetryData = this._telemetryData.asReadonly();
  readonly mapboxKeys = this._mapboxKeys.asReadonly();
  readonly sensorsWithTelemetry = this._sensorsWithTelemetry.asReadonly();

  // ✅ Computed: Filtered notifications
  readonly filteredNotifications = computed(() => {
    const notifications = this._notifications();
    const navigation = this._navigation();
    const searchTerm = this._searchTerm();
    const filter = this.filter();

    if (notifications.length === 0 || navigation.length === 0) {
      return { notifications: [], filtered: this.filtered() };
    }

    const filtered = this.filterNotifications(notifications, navigation, searchTerm, filter);

    return { notifications: filtered, filtered: this.filtered() };
  });

  readonly delayedLoadNavigation = (delay = 5000, force = true) => {
    setTimeout(() => this.loadNavigation(force), delay);
  };

  // ✅ Computed: Filtered state
  readonly filtered = computed(
    () => (!!this._searchTerm() && this._searchTerm().length > 0) || this._filter().IsFiltering
  );

  // ✅ Computed: Filter settings count
  readonly filterSettingsCount = computed(() => this._filter().FilterSettingsCount);

  // ✅ Computed: Debug mode
  readonly debugMode = computed(() => this.cfg.debugMode);

  // ✅ Computed: Check if current app is Equipment Monitor
  readonly isEquipmentMonitor = computed(() => this.cfg.appName === 'em');

  // ✅ Computed: Unfiltered regions
  readonly unfilteredRegions = computed(() => this._regions());

  // ✅ Computed: Unfiltered areas (countries)
  readonly unfilteredAreas = computed(() =>
    this._regions()
      .map((region) => region.children)
      .flat(2)
  );

  // ✅ Computed: Filtered regions with search and filter applied
  readonly filteredRegions = computed(() => {
    const regions = this._regions();
    const term = this._searchTerm();
    const filterObj = this._filter();

    return this.applySearchAndFilter(regions, term, filterObj);
  });

  // ✅ Computed: Plants (flattened from filtered regions)
  readonly plants = computed(() => {
    const filtered = this.filteredRegions();
    const allPlants = filtered
      .map((region: Location) => region.children)
      .flat(2)
      .map((country: Location) => country.children)
      .flat(2);
    // EM-specific: Only show plants that have at least one lifetime-connected equipment
    // (never-connected plants should not appear in the plant list)
    return this.isEquipmentMonitor()
      ? allPlants.filter(
          (plant: Location) =>
            plant.Assets.some((a: any) => a.isLifetimeConnected) || plant.filtered
        )
      : allPlants;
  });

  // ✅ Computed: Regions state (for backward compatibility)
  readonly regionsState = computed(() => ({
    regions: this.filteredRegions(),
    loadingComplete: !this._navInProgress(),
    filtered: this.filtered(),
  }));

  // ✅ Computed: Unfiltered Regions state (for filter component to show all options)
  readonly unfilteredRegionsState = computed(() => ({
    regions: this._regions(),
    loadingComplete: !this._navInProgress(),
    filtered: false,
  }));

  // ✅ Computed: Plants state (for backward compatibility)
  readonly plantsState = computed(() => {
    const plants =
      this.cfg.appName === 'em'
        ? this.plants()?.filter((plant: any) => plant?.Assets?.length > 0)
        : this.plants();
    return {
      plants: plants,
      loadingComplete: !this._navInProgress(),
      filtered: this.filtered(),
    };
  });

  // ✅ Computed: Sensors state (for backward compatibility)
  readonly sensorsState = computed(() => ({
    sensors: this._sensors(),
    loadingComplete: !this._sensorsInProgress(),
    filtered: null,
  }));

  /**
   * ✅ Action: Load navigation data from API
   */
  async loadNavigation(forceReload = false): Promise<Location[]> {
    // Prevent multiple simultaneous calls
    if (this._navInProgress() && !forceReload) {
      return this._regions();
    }

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    const sortFn = (a: IOTObject, b: IOTObject) => {
      if (a.isAsset !== b.isAsset) return a.isAsset ? 1 : -1;
      return collator.compare(a.objectName, b.objectName);
    };

    if (this._navigation().length === 0 || forceReload || this._lazyReload()) {
      this._navInProgress.set(true);
      this._lazyReload.set(false);
      this._loading.set(false);

      try {
        const response = await this.assetsApi.getNavigation(true);

        // Handle both response formats: array directly OR { items: [], storageOptions: ... }
        const isArrayResponse = Array.isArray(response);
        const items = isArrayResponse ? response : response.items || [];
        const storageOptions = isArrayResponse ? null : response.storageOptions;

        // Store storage options
        this._storageOptions.set(storageOptions);

        // Extract and process navigation items
        const deduped = this.removeDuplicateRegions(items);

        // ✅ EM-specific: Override object status based on current notifications
        const currentNotifications = this._notifications();
        const withStatus =
          this.isEquipmentMonitor() && currentNotifications.length > 0
            ? this.overrideObjectStatusByNotifications(deduped, currentNotifications)
            : deduped;

        this._navigation.set(withStatus);
        this.matchSensorsToAssets();

        // Build regions
        const regionObjects = withStatus
          .filter((item: IOTObject) => item.curLayer === 'a')
          .sort(sortFn)
          .map((item: IOTObject) => new Location(item, withStatus))
          .filter((item: Location) => !!item);

        this._regions.set(regionObjects);
        this._navInProgress.set(false);

        return regionObjects;
      } catch (error: any) {
        console.error('Failed to load navigation:', error);
        this._navInProgress.set(false);
        this._regions.set([]);

        // Re-throw authentication errors to allow error interceptor to handle redirect
        if (error?.status === 401 || error?.statusCode === 401) {
          throw error;
        }

        return [];
      }
    } else {
      // Return cached data

      // ✅ EM-specific: Override object status based on current notifications
      const currentNotifications = this._notifications();
      const navData = this._navigation();
      const withStatus =
        this.isEquipmentMonitor() && currentNotifications.length > 0
          ? this.overrideObjectStatusByNotifications(navData, currentNotifications)
          : navData;

      const regionObjects = withStatus
        .filter((item: IOTObject) => item?.curLayer === 'a')
        .sort(sortFn)
        .map((item: IOTObject) => new Location(item, withStatus))
        .filter((item: Location) => !!item);

      this._regions.set(regionObjects);
      return regionObjects;
    }
  }

  /**
   * ✅ Action: Load sensors from API
   */
  async loadSensors(forceReload = false): Promise<Sensor[]> {
    // Prevent multiple simultaneous calls
    if (this._sensorsInProgress()) {
      return this._sensors();
    }

    if (this._sensors().length === 0 || forceReload || this._lazyReload()) {
      this._sensorsInProgress.set(true);
      this._lazyReload.set(false);

      try {
        const items = await this.assetsApi.getSensors();
        const result = items.map((item: any) => new Sensor(item));

        this._sensors.set(result);
        this.matchSensorsToAssets();
        this._sensorsInProgress.set(false);

        return result;
      } catch (error: any) {
        console.error('Failed to load sensors:', error);
        this._sensorsInProgress.set(false);

        // Re-throw authentication errors to allow error interceptor to handle redirect
        if (error?.status === 401 || error?.statusCode === 401) {
          throw error;
        }

        return [];
      }
    } else {
      return this._sensors();
    }
  }

  async loadRegions(forceReload = false): Promise<Location[]> {
    const tasks: Promise<any>[] = [this.loadNavigation(forceReload)];

    // EM app does not have a sensors list endpoint — skip loadSensors
    if (!this.isEquipmentMonitor()) {
      tasks.push(this.loadSensors(forceReload));
    }

    await Promise.all(tasks);
    return this._regions();
  }

  updateSearchTerm(term: string): void {
    this._searchTerm.set(term);

    // ✅ EM-specific: Trigger region and notification refresh
    if (this.isEquipmentMonitor()) {
      setTimeout(() => {
        this.loadRegions();
      }, 1);
    }
  }

  updateFilter(newFilter: LocationFilter): void {
    // Create a proper LocationFilter instance to preserve getters/setters
    const updatedFilter = new LocationFilter({
      ...newFilter,
      preselection: this._selectionPreset().length,
    });
    this._filter.set(updatedFilter);
    // Computed signals (filteredRegions, plants) react automatically to filter changes
  }

  updateSelectionPreset(preset: Location[]): void {
    const currentIds = this._selectionPreset()
      .map((item) => item.objectID)
      .join(';');
    const newIds = preset.map((item) => item.objectID).join(';');

    if (currentIds === newIds) return;

    this._selectionPreset.set(preset);

    const updatedFilter = new LocationFilter({
      ...this._filter(),
      preselection: preset.length,
    });
    this._filter.set(updatedFilter);
  }

  resetLocationFilter(): void {
    if (this._filter().location.length === 0) return;

    const updatedFilter = new LocationFilter({
      ...this._filter(),
      location: [],
    });
    this.updateFilter(updatedFilter);
  }

  clearFilter(): void {
    this.updateFilter(new LocationFilter());
  }

  clear(): void {
    this._nextObjectId.set(0);
    this._navigation.set([]);
    this._regions.set([]);
    this._sensors.set([]);
    this._searchTerm.set(GLOBAL_CONSTANT.EMPTY_STRING);
    this._filter.set(new LocationFilter());
    this._selectionPreset.set([]);
    this._loading.set(false);
    this.clearNanoPreciseCache();
  }

  /**
   * ✅ Alias for clear() - backward compatibility with NavigationStore
   */
  resetState(): void {
    this.clear();
  }

  /**
   * ✅ Action: Get next object ID (for creating temporary objects)
   */
  getNextObjectId(): number {
    this._nextObjectId.update((id) => id - 1);
    return this._nextObjectId();
  }

  /**
   * ✅ Action: Set NanoPrecise data
   */
  setRotatingEquipmentDetail(post: unknown): void {
    this._nanoPrecisePosts.set(post);
  }

  /**
   * ✅ Action: Get NanoPrecise data
   */
  getRotatingEquipmentDetail(): unknown {
    return this._nanoPrecisePosts();
  }

  /**
   * ✅ Action: Update building plan
   */
  updateBuildingPlan(objectID: number, buildingPlan: string, buildingPlanName: string): void {
    this._navigation.update((nav) => {
      const idx = nav.findIndex((x) => x.objectID === objectID);
      if (idx >= 0) {
        nav[idx].buildingPlan = buildingPlan;
        nav[idx].buildingPlanName = buildingPlanName;
      }
      return [...nav];
    });
  }

  /**
   * ✅ Utility: Get asset by ID from locations
   */
  getAsset(id: number, locations: Location[]): any {
    const assets = locations.map((plant: Location) => plant.Assets).flat(2);
    return assets.find((asset: any) => asset.objectID === id);
  }

  /**
   * ✅ Utility: Get children by parent ID
   */
  getChildrenByObjectID(parentId: number): IOTObject[] {
    return this._navigation().filter((x) => x?.parentID === parentId);
  }

  /**
   * ✅ Utility: Get plant by asset ID
   */
  getPlantByAssetID(id: number, locations: Location[]): Location | undefined {
    const plants = locations;
    return plants.find((p: Location) => {
      const assets = p.Assets.filter((asset: any) => asset.objectID === id);
      return assets.length > 0;
    });
  }

  /**
   * ✅ Utility: Get sensors by asset ID
   */
  getSensorsByAssetID(assetId: number): Sensor[] {
    return this._sensors().filter((sensor) => sensor.parentID === assetId);
  }

  /**
   * ✅ Utility: Get sensor by sensor ID
   */
  getSensorBySensorID(sensorId: number): Sensor | undefined {
    return this._sensors().find((sensor) => sensor.sensorID === sensorId);
  }

  /**
   * Private: Remove duplicate regions
   */
  private removeDuplicateRegions(objects: IOTObject[]): IOTObject[] {
    function findDuplicate(item: IOTObject, index: number, array: IOTObject[]): number {
      if (item.curLayer !== 'a' && item.curLayer !== 'b') return -1;
      const temp = array.findIndex((_item) => _item.objectName === item.objectName);
      if (index !== temp) return array[temp].objectID;
      else return -1;
    }

    const idMapping: Record<number, number> = {};
    const output = objects.filter((item, index, array) => {
      const duplicate = findDuplicate(item, index, array);
      if (duplicate >= 0) {
        idMapping[item.objectID] = duplicate;
        return false;
      } else {
        return true;
      }
    });

    return output.map((item) => {
      item.parentID = idMapping[item.parentID] ?? item.parentID;
      return item;
    });
  }

  private overrideObjectStatusByNotifications(
    objects: IOTObject[],
    notifications: Notification[]
  ): IOTObject[] {
    return objects.map((item: IOTObject) => {
      if (!item.inactive) {
        const inactiveNotif = notifications.find(
          (_item) => _item.objectID === item.objectID && _item.inactive
        );
        if (inactiveNotif) {
          item.critical = false;
          item.warning = false;
          item.inactive = true;
        } else if (!item.critical) {
          const criticalNotif = notifications.find(
            (_item) => _item.objectID === item.objectID && _item.isCritical && !_item.inactive
          );
          if (criticalNotif) {
            item.critical = true;
            item.warning = false;
            item.inactive = false;
          } else {
            const warningNotif = notifications.find(
              (_item) => _item.objectID === item.objectID && _item.isWarning && !_item.inactive
            );
            if (warningNotif) {
              item.warning = true;
            }
          }
        }
      }

      return item;
    });
  }

  /**
   * Private: Match sensors to their parent assets
   */
  private matchSensorsToAssets(): void {
    const sensorsList = this._sensors();
    const navList = this._navigation();

    if (sensorsList.length > 0 && navList.length > 0) {
      navList.forEach((location) => {
        if (location.isAsset) {
          location.sensors = sensorsList.filter((sensor) => sensor.parentID === location.objectID);
          location.sensors.forEach((sensor) => {
            sensor.parentEquipmentType = location.equipmentType;
          });
        }
      });

      this._navigation.set([...navList]);
    }
  }

  /**
   * Private: Apply search and filter to locations
   */
  private applySearchAndFilter(
    list: Location[],
    searchTerm: string,
    filter: LocationFilter
  ): Location[] {
    // Handle country/region preselection
    let updatedFilter = filter;
    if (filter.countryOrRegionSelected) {
      const regionMatch = list.find((item) => item.objectID === filter.countryOrRegionSelected);

      if (regionMatch) {
        const newFilter = new LocationFilter(filter);
        newFilter.location = [regionMatch.objectID];
        updatedFilter = newFilter;
      } else {
        const countries = list.map((item) => item.children).flat(2);
        const countryMatch = countries.find(
          (item) => item.objectID === filter.countryOrRegionSelected
        );

        if (countryMatch) {
          const region = list.find((item) =>
            item.children.some((child: Location) => child.objectID === countryMatch.objectID)
          );

          if (region) {
            const newFilter = new LocationFilter(filter);
            newFilter.location = [region.objectID, countryMatch.objectID];
            updatedFilter = newFilter;
          }
        }
      }
    }

    // Apply search
    const searched = searchTerm
      ? list
          .map((item) => item.ApplySearch(searchTerm))
          .filter((item): item is Location => item !== null)
      : list;

    // Apply filter
    const filtered = updatedFilter.IsFiltering
      ? searched
          .map((item) => item.ApplyFilter(updatedFilter))
          .filter((item): item is Location => item !== null)
      : searched;

    return filtered;
  }

  /**
   * ✅ Action: Trigger lazy reload on next navigation/sensor request
   */
  triggerLazyReload(): void {
    this._lazyReload.set(true);
  }

  async getAssetHealth(id: number): Promise<any> {
    const response = await this.assetsApi.getAssetHealth(id);
    const asset = AssetDetail.FromServiceResponse(response);
    return asset.health;
  }

  async getSensorColorData(
    macID: string,
    modelID: string,
    assetID: string,
    gatewayID: string
  ): Promise<any[]> {
    return this.assetsApi.getSensorColorData(macID, modelID, assetID, gatewayID);
  }

  async postHtstData(
    colorCode: string,
    macId: string,
    modelId: string,
    isOccupied: boolean
  ): Promise<any> {
    return this.assetsApi.postHtstData(colorCode, macId, modelId, isOccupied);
  }

  // async getMapBoxKeys(): Promise<{
  //   satelliteView: string;
  //   mapView: string;
  //   geoCodingToken: string;
  // }> {
  //   return this.assetsApi.getMapBoxKeys();
  // }

  async getCountryList(): Promise<any[]> {
    return this.assetsApi.getCountryList();
  }

  async getParentCountryList(): Promise<any[]> {
    return this.assetsApi.getParentCountryList();
  }

  async getBuildingAreaSettings(objectID: number): Promise<BuildingAreaSettings> {
    const response = await this.assetsApi.getBuildingAreaSettings(objectID);
    const settings: BuildingAreaSettings = {
      objectID,
      structureFloorZone: response?.hasFloorLayer ?? false,
      structureSectionCrossing: response?.hasSectionLayer ?? false,
      structureAssociatedEquipment: response?.hasAssociatedEquipmentLayer ?? false,
    };
    return settings;
  }

  async setBuildingAreaSettings(
    objectID: number,
    structureFloorZone: boolean,
    structureSectionCrossing: boolean,
    structureAssociatedEquipment: boolean
  ): Promise<boolean> {
    await this.assetsApi.setBuildingAreaSettings({
      objectID,
      structureFloorZone,
      structureSectionCrossing,
      structureAssociatedEquipment,
    });
    return true;
  }

  /**
   * Create plant
   * @returns Promise<Location> - newly created plant
   */
  async createPlant(
    plantName: string,
    areaID: number,
    areaName: string,
    imageFileName: string,
    imageData: string
  ): Promise<Location> {
    const resultID = await this.assetsApi.createPlant({
      plantName,
      areaID,
      imageFileName,
      imageData,
    });

    // Optimistically add new plant to navigation (like Angular 12)
    const newPlant: IOTObject = {
      objectID: resultID,
      objectName: plantName,
      parentID: areaID,
      curLayer: 'c',
      nextLayer: 'd',
      isAsset: true,
      picture: imageData,
      thumbnail: imageData,
      objectTypeID: 0,
      objectType: '',
      objectStatus: '',
      colorCode: '',
      description: '',
      isConnected: false,
      inactive: false,
      warning: false,
      critical: false,
      equipmentType: '',
      childCount: 0,
      geoPosition: '',
      layerName: '',
      layerID: 0,
      buildingPlan: '',
      buildingPlanName: '',
      customerName: '',
      sensorDigitIDs: '',
      edgeDeviceIDs: '',
      macs: '',
      gatewayIDs: '',
    };

    // Resolve duplicate regions for the new plant
    const modifiedParents = this._navigation().filter((o) => o.objectName === areaName);
    if (modifiedParents.length > 0) {
      newPlant.parentID = modifiedParents[0].objectID;
    }

    // Immediately add to navigation signal
    this._navigation.update((nav) => [...nav, newPlant]);

    // Rebuild regions to reflect the new plant
    const currentNav = this._navigation();
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    const sortFn = (a: IOTObject, b: IOTObject) => {
      if (a.isAsset !== b.isAsset) return a.isAsset ? 1 : -1;
      return collator.compare(a.objectName, b.objectName);
    };

    const regionObjects = currentNav
      .filter((item: IOTObject) => item.curLayer === 'a')
      .sort(sortFn)
      .map((item: IOTObject) => new Location(item, currentNav))
      .filter((item: Location) => !!item);

    this._regions.set(regionObjects);

    // Delayed reload to allow backend to commit the new plant (fire-and-forget after 1 second)
    this.delayedLoadNavigation();

    return new Location(newPlant, [], []);
  }

  /**
   * Update plant
   * @returns Promise<any[]> - update result
   */
  async updatePlant(plant: Location): Promise<any[]> {
    const flatLocations = this.flattenPlant(plant);
    const otObjects: IOTObject[] = [];
    flatLocations.forEach((element) => {
      if (element.Dirty) {
        const ot: IOTObject = {
          objectID: element.objectID,
          objectName: element.objectName,
          parentID: element.parentID,
          curLayer: element.curLayer,
          nextLayer: element.nextLayer,
          delete: element.delete,
          displayOrder: element.displayOrder,
          objectTypeID: element.objectTypeID,
          objectType: element.objectType,
          objectStatus: element.objectStatus,
          colorCode: element.colorCode,
          description: element.description,
          isAsset: element.isAsset,
          isConnected: element.isConnected,
          inactive: element.inactive,
          warning: element.warning,
          critical: element.critical,
          equipmentType: element.equipmentType,
          childCount: element.childCount,
          geoPosition: element.geoPosition,
          picture: element.picture,
          thumbnail: element.thumbnail,
          layerName: element.layerName,
          layerID: element.layerID,
          buildingPlan: element.buildingPlan,
          buildingPlanName: element.buildingPlanName,
          customerName: element.customerName,
          sensorDigitIDs: element.sensorDigitIDs,
          edgeDeviceIDs: element.edgeDeviceIDs,
          macs: element.macs,
          gatewayIDs: element.gatewayIDs,
        };
        otObjects.push(ot);
      }
    });
    if (otObjects.length === 0) {
      return Promise.resolve([]);
    }
    const result = await this.assetsApi.updatePlant(otObjects);

    // Optimistically update navigation without waiting for reload (like Angular 12)
    // result is a Record mapping old objectID (as string) to new objectID
    const resultMap = result as any;
    otObjects.forEach((obj) => {
      if (obj.delete) {
        // Remove from navigation
        this._navigation.update((nav) => nav.filter((x) => x.objectID !== obj.objectID));
      } else {
        const newObjectID = resultMap[obj.objectID.toString()];
        const idx = this._navigation().findIndex((x) => x.objectID === newObjectID);

        if (idx !== -1) {
          // Update existing object
          this._navigation.update((nav) => {
            const updated = [...nav];
            updated[idx] = {
              ...updated[idx],
              objectName: obj.objectName,
              nextLayer: obj.nextLayer,
            };
            return updated;
          });
        } else {
          // Add new object to navigation
          const created: IOTObject = {
            objectID: newObjectID,
            objectName: obj.objectName,
            parentID: obj.parentID > 0 ? obj.parentID : resultMap[obj.parentID.toString()],
            curLayer: obj.curLayer,
            nextLayer: obj.nextLayer,
            layerID: obj.layerID,
            displayOrder: obj.displayOrder,
            objectTypeID: obj.objectTypeID,
            objectType: obj.objectType,
            objectStatus: obj.objectStatus,
            colorCode: obj.colorCode,
            description: obj.description,
            isAsset: !otObjects.some((c) => c.parentID === obj.objectID),
            isConnected: obj.isConnected,
            inactive: obj.inactive,
            warning: obj.warning,
            critical: obj.critical,
            equipmentType: obj.equipmentType,
            childCount: obj.childCount,
            geoPosition: obj.geoPosition,
            picture: obj.picture,
            thumbnail: obj.thumbnail,
            layerName: obj.layerName,
            buildingPlan: obj.buildingPlan,
            buildingPlanName: obj.buildingPlanName,
            customerName: obj.customerName,
            sensorDigitIDs: obj.sensorDigitIDs,
            edgeDeviceIDs: obj.edgeDeviceIDs,
            macs: obj.macs,
            gatewayIDs: obj.gatewayIDs,
          };
          this._navigation.update((nav) => [...nav, created]);
        }
      }
    });

    // Then do the full reload in the background for consistency
    return result;
  }

  async deletePlant(plantID: number): Promise<any> {
    const result = await this.assetsApi.deletePlant(plantID);

    // Optimistically remove plant from navigation (like Angular 12 tap)
    this._navigation.update((nav) => nav.filter((x) => x.objectID !== plantID));

    //Rebuild regions to reflect the deletion
    // const currentNav = this._navigation();
    // const collator = new Intl.Collator(undefined, {
    //   numeric: true,
    //   sensitivity: 'base',
    // });
    // const sortFn = (a: IOTObject, b: IOTObject) => {
    //   if (a.isAsset !== b.isAsset) return a.isAsset ? 1 : -1;
    //   return collator.compare(a.objectName, b.objectName);
    // };

    // const regionObjects = currentNav
    //   .filter((item: IOTObject) => item.curLayer === 'c')
    //   .sort(sortFn)
    //   .map((item: IOTObject) => new Location(item, currentNav))
    //   .filter((item: Location) => !!item);

    // this._regions.set(regionObjects);

    // Then reload in background for consistency (fire-and-forget)
    //this.delayedLoadNavigation();
    this.loadNavigation(true);

    return result;
  }

  async updatePlantMetadata(
    plantID: number,
    plantName: string,
    locationID: number,
    imageFileName: string,
    imageData: string,
    useDefaultImage: boolean,
    oldPicture: string
  ): Promise<{ StorageLink: string; PlantID: number }> {
    const result = await this.assetsApi.updatePlantMetadata({
      plantID,
      plantName,
      locationID,
      imageFileName,
      imageData,
      useDefaultImage,
      oldPicture,
    });

    // Optimistically update navigation (like Angular 12 tap)
    this._navigation.update((nav) => {
      const idx = nav.findIndex((x) => x.objectID === plantID);

      if (idx >= 0) {
        nav[idx].objectName = plantName;

        // instead of updating the parentID to the provided locationID, we are assigning for the "main" locationID that is used instead (see removeDuplicateRegions)
        const countryName = nav.find((x) => x.objectID === locationID)?.objectName;
        const modifiedParent = countryName
          ? nav.find((x) => x.curLayer === 'b' && x.objectName === countryName)
          : null;
        if (modifiedParent) nav[idx].parentID = modifiedParent.objectID;

        if (result.StorageLink && result.StorageLink.length > 0) {
          nav[idx].picture = result.StorageLink;
          nav[idx].thumbnail = result.StorageLink;
        }
      }

      return [...nav];
    });

    // Rebuild regions to reflect the updated plant (name/parent might have changed)
    const currentNav = this._navigation();
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    const sortFn = (a: IOTObject, b: IOTObject) => {
      if (a.isAsset !== b.isAsset) return a.isAsset ? 1 : -1;
      return collator.compare(a.objectName, b.objectName);
    };

    const regionObjects = currentNav
      .filter((item: IOTObject) => item.curLayer === 'a')
      .sort(sortFn)
      .map((item: IOTObject) => new Location(item, currentNav))
      .filter((item: Location) => !!item);

    this._regions.set(regionObjects);

    // Then reload in background for consistency (fire-and-forget)
    this.delayedLoadNavigation();

    return result;
  }

  async createSensor(
    parentID: number,
    imageFileName: string,
    imageData: string,
    sensorName: string,
    qrDigits: number,
    qrEdge: string | number,
    sensorX: number,
    sensorY: number,
    equipmentSensorX: number,
    equipmentSensorY: number,
    sensorLabel: string,
    junctionBoxSlot: string,
    geoPosition: string,
    previousSensorId: number,
    sensorType = 1003,
    sensorAdditionalFieldsValues: any[] = []
  ): Promise<any[]> {
    const result = await this.assetsApi.createSensor({
      parentID,
      imageFileName,
      imageData,
      sensorName,
      qrDigits,
      qrEdge,
      sensorX,
      sensorY,
      equipmentSensorX,
      equipmentSensorY,
      sensorLabel,
      junctionBoxSlot,
      sensorType,
      geoPosition,
      previousSensorId,
      sensor_additional_fields: JSON.stringify(sensorAdditionalFieldsValues),
    });

    const sensor = new Sensor(result[0]);
    const nav = this._navigation();

    for (const element of nav) {
      if (element.objectID === sensor.parentID) {
        if (!element.sensors) {
          element.sensors = [];
        }
        sensor.parentEquipmentType = element.equipmentType;
        element.sensors.push(sensor);
        break;
      }
    }

    this._navigation.set([...nav]);
    this._sensors.update((sensors) => [...sensors, sensor]);

    return result;
  }

  async updateSensor(
    equipmentID: number,
    sensorID: number,
    sensorX: number,
    sensorY: number,
    sensorLabel: string,
    junctionBoxSlot: string,
    geoPosition: string,
    imageFileName: string,
    imageData: string,
    sensorName: string,
    useDefaultImage: boolean,
    oldPicture: string,
    subscription: string,
    isApplicableForAll = false
  ): Promise<any[]> {
    const result = await this.assetsApi.updateSensor({
      sensorID,
      sensorName,
      sensorX,
      sensorY,
      sensorLabel,
      junctionBoxSlot,
      geoPosition,
      imageFileName,
      imageData,
      useDefaultImage,
      oldPicture,
      subscription,
      isApplicableForAll,
    });

    this.replaceSensor(equipmentID, sensorID, new Sensor(result[0]));
    return result;
  }

  async deleteSensor(sensorID: number): Promise<any[]> {
    const result = await this.assetsApi.deleteSensor(sensorID);

    this._sensors.update((sensors) => sensors.filter((sensor) => sensor.sensorID !== sensorID));
    this._navigation.update((nav) =>
      nav.map((item) => ({
        ...item,
        sensors: item.sensors?.filter((sensor) => sensor.sensorID !== sensorID) ?? [],
      }))
    );

    return result;
  }

  async reconnectSensor(
    parentID: number,
    sensorID: number,
    useDefaultImage: boolean,
    oldPicture: string,
    imageFileName: string,
    imageData: string,
    sensorName: string,
    qrDigits: number,
    qrEdge: number | string,
    sensorX: number,
    sensorY: number,
    equipmentSensorX: number,
    equipmentSensorY: number,
    sensorLabel: string,
    junctionBoxSlot: string,
    geoPosition: string
  ): Promise<any[]> {
    const result = await this.assetsApi.reconnectSensor({
      parentID,
      sensorID,
      sensorName,
      qrDigits,
      qrEdge,
      sensorX,
      sensorY,
      sensorLabel,
      equipmentSensorX,
      equipmentSensorY,
      geoPosition,
      junctionBoxSlot,
      imageFileName,
      imageData,
      useDefaultImage,
      oldPicture,
    });

    const newSensor = new Sensor(result[0]);

    this._sensors.update((sensors) => {
      const idx = sensors.findIndex((x) => x.sensorID === sensorID);
      if (idx >= 0) {
        sensors[idx] = newSensor;
      }
      return [...sensors];
    });

    this._navigation.update((nav) => {
      const parent = nav.find((x) => x.objectID === parentID);
      if (parent && parent.sensors) {
        const parentIdx = parent.sensors.findIndex((x) => x.sensorID === sensorID);
        if (parentIdx >= 0) {
          parent.sensors[parentIdx] = newSensor;
        }
      }
      return [...nav];
    });

    return result;
  }

  async createEquipment(
    parentID: number,
    imageFileName: string,
    imageData: string,
    equipmentName: string,
    equipmentType: string,
    rotatingEqDefaultImageId: string
  ): Promise<any[]> {
    const result = await this.assetsApi.createEquipment({
      parentID,
      imageFileName,
      imageData,
      equipmentName,
      equipmentType,
      rotatingEqDefaultImageId,
    });

    // Optimistically add new equipment to navigation
    this._navigation.update((nav) => [...nav, result[0]]);

    // Rebuild regions to reflect the new equipment
    const currentNav = this._navigation();
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    const sortFn = (a: IOTObject, b: IOTObject) => {
      if (a.isAsset !== b.isAsset) return a.isAsset ? 1 : -1;
      return collator.compare(a.objectName, b.objectName);
    };

    const regionObjects = currentNav
      .filter((item: IOTObject) => item?.curLayer === 'a')
      .sort(sortFn)
      .map((item: IOTObject) => new Location(item, currentNav))
      .filter((item: Location) => !!item);

    this._regions.set(regionObjects);

    // Delayed reload to allow backend to commit the new equipment (fire-and-forget after 1 second)
    this.delayedLoadNavigation();

    return result;
  }

  async updateEquipment(
    equipmentID: number,
    imageFileName: string,
    imageData: string,
    equipmentName: string,
    equipmentType: string,
    useDefaultImage: boolean,
    oldPicture: string,
    rotatingEqDefaultImageId?: string
  ): Promise<any[]> {
    const result: any = await this.assetsApi.updateEquipment({
      equipmentID,
      imageFileName: imageFileName.length ? imageFileName : null,
      imageData: imageData,
      equipmentName,
      equipmentType,
      useDefaultImage,
      oldPicture,
      rotatingEqDefaultImageId: rotatingEqDefaultImageId || '',
    });

    // Optimistically update navigation (like Angular 12 tap)
    this._navigation.update((nav) => {
      const idx = nav.findIndex((x) => x.objectID === equipmentID);
      if (idx >= 0) {
        nav[idx] = {
          ...nav[idx],
          objectName: equipmentName,
          defaultImageId: rotatingEqDefaultImageId,
          picture: result?.storageLink || imageData,
          thumbnail: result?.storageLink || imageData,
        };
      }
      return [...nav];
    });

    // Rebuild regions to reflect the updated equipment (name might have changed)
    const currentNav = this._navigation();
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    const sortFn = (a: IOTObject, b: IOTObject) => {
      if (a.isAsset !== b.isAsset) return a.isAsset ? 1 : -1;
      return collator.compare(a.objectName, b.objectName);
    };

    const regionObjects = currentNav
      .filter((item: IOTObject) => item.curLayer === 'a')
      .sort(sortFn)
      .map((item: IOTObject) => new Location(item, currentNav))
      .filter((item: Location) => !!item);

    this._regions.set(regionObjects);

    // Then reload in background for consistency (fire-and-forget)
    this.delayedLoadNavigation();

    return result;
  }

  async updateEquipmentMarker(equipmentID: number, markers: IFlatMarker[]): Promise<any[]> {
    const result = await this.assetsApi.updateEquipmentMarker(markers);

    this._navigation.update((nav) => {
      const idx = nav.findIndex((x) => x.objectID === equipmentID);
      if (idx >= 0 && nav[idx].sensors) {
        markers.forEach((marker) => {
          const sensorIdx = nav[idx].sensors!.findIndex((x) => x.sensorID === marker.sensorId);
          if (sensorIdx >= 0) {
            nav[idx].sensors![sensorIdx].equipmentSensorX = marker.x;
            nav[idx].sensors![sensorIdx].equipmentSensorY = marker.y;
          }
        });
      }
      return [...nav];
    });

    this._sensors.update((sensors) => {
      markers.forEach((marker) => {
        const idx = sensors.findIndex((x) => x.sensorID === marker.sensorId);
        if (idx >= 0) {
          sensors[idx].equipmentSensorX = marker.x;
          sensors[idx].equipmentSensorY = marker.y;
        }
      });
      return [...sensors];
    });

    return result;
  }

  async deleteEquipment(equipmentID: number): Promise<any> {
    const result = await this.assetsApi.deleteEquipment(equipmentID);

    // Optimistically remove equipment from navigation (like Angular 12 tap)
    this._navigation.update((nav) => nav.filter((x) => x.objectID !== equipmentID));

    // Rebuild regions to reflect the deletion
    const currentNav = this._navigation();
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    const sortFn = (a: IOTObject, b: IOTObject) => {
      if (a.isAsset !== b.isAsset) return a.isAsset ? 1 : -1;
      return collator.compare(a.objectName, b.objectName);
    };

    const regionObjects = currentNav
      .filter((item: IOTObject) => item.curLayer === 'a')
      .sort(sortFn)
      .map((item: IOTObject) => new Location(item, currentNav))
      .filter((item: Location) => !!item);

    this._regions.set(regionObjects);

    // Then reload in background for consistency (fire-and-forget)
    this.delayedLoadNavigation();

    return result;
  }

  getAdditionalInfoTypes() {
    return this.assetsApi.getAdditionalInfoTypes();
  }

  getAdditionalInfo(objectID: number) {
    return this.assetsApi.getAdditionalInfo(objectID);
  }

  updateAdditionalInfo(addInfoValues: any[]) {
    const newAddInfoValues = addInfoValues.map((item) => {
      if (item.unit) item.unit = JSON.stringify(item.unit);
      return item;
    });
    return this.assetsApi.updateAdditionalInfo(newAddInfoValues);
  }

  getSensorAdditionalInfo(sensorID: number) {
    return this.assetsApi.getSensorAdditionalInfo(sensorID);
  }

  checkDeviceMapping(deviceValue: string, deviceType: string) {
    return this.assetsApi.checkDeviceMapping(deviceValue, deviceType);
  }

  reserveGatewaySlot(gateway: string, isMT: boolean, isHtst: boolean) {
    return this.assetsApi.reserveGatewaySlot(gateway, isMT, isHtst);
  }

  checkGatewayStatusMoxa(gateway: string) {
    return this.assetsApi.checkGatewayStatusMoxa(gateway);
  }

  checkGatewayConnectivity(gateway: string) {
    return this.assetsApi.checkGatewayConnectivity(gateway);
  }

  /**
   * Check gateway connectivity (Moxa)
   */
  checkGatewayConnectivityMoxa(gateway: string, dau: string) {
    return this.assetsApi.checkGatewayConnectivityMoxa(gateway, dau);
  }

  /**
   * Check DAU connectivity
   */
  checkDauConnectivity(gateway: string, slot: number) {
    return this.assetsApi.checkDauConnectivity(gateway, slot);
  }

  /**
   * Configure gateway
   */
  configureGateway(
    inletType: string,
    outletType: string,
    gateway: string,
    dau: string,
    slot: string
  ) {
    return this.assetsApi.configureGateway({
      inletType,
      outletType,
      gateway,
      dau,
      slot,
    });
  }

  /**
   * Configure gateway (Moxa)
   */
  configureGatewayMoxa(
    gateway: string,
    inletSerialNumber: string,
    inletPartNumber: string,
    outletSerialNumber: string,
    outletPartNumber: string,
    dau: string,
    dauPartNumber: string,
    slot: string
  ) {
    return this.assetsApi.configureGatewayMoxa({
      gateway,
      inletSerialNumber,
      inletPartNumber,
      outletSerialNumber,
      outletPartNumber,
      dau,
      dauPartNumber,
      slot,
    });
  }

  /**
   * Configure gateway (Moxa HTST)
   */
  configureGatewayMoxaHTST(
    inletGateway: string,
    outletGateway: string,
    inletSerialNumber: string,
    inletPartNumber: string,
    outletSerialNumber: string,
    outletPartNumber: string,
    inletDau: string,
    outletDau: string,
    inletDauPartNumber: string,
    outletDauPartNumber: string,
    inletSensorSlot: string,
    outletSensorSlot: string,
    inletGatewayslot: string,
    outletGatewaySlot: string
  ) {
    return this.assetsApi.configureGatewayMoxaHTST({
      inletGateway,
      outletGateway,
      inletSerialNumber,
      inletPartNumber,
      outletSerialNumber,
      outletPartNumber,
      inletDau,
      outletDau,
      inletDauPartNumber,
      outletDauPartNumber,
      inletSensorSlot,
      outletSensorSlot,
      inletGatewayslot,
      outletGatewaySlot,
    });
  }

  /**
   * Remove DAU from gateway (Moxa)
   */
  removeDAUFromGatewayMoxa(sensorID: number) {
    return this.assetsApi.removeDAUFromGatewayMoxa(sensorID);
  }

  /**
   * Match sensor
   * @returns Promise<any[]> - match result
   */
  async matchSensor(sensorID: number, qrNewEdgeDigits: string): Promise<any[]> {
    const result = await this.assetsApi.matchSensor(sensorID, qrNewEdgeDigits);
    this.replaceSensor(result[0].parentID, sensorID, new Sensor(result[0]));
    return result;
  }

  /**
   * Match all sensors
   * @returns Promise<any[]> - match results
   */
  async matchAllSensors(qrOldEdgeDigits: string, qrNewEdgeDigits: string): Promise<any[]> {
    const result = await this.assetsApi.matchAllSensors(qrOldEdgeDigits, qrNewEdgeDigits);
    result.forEach((sensor) => {
      this.replaceSensor(sensor.parentID, sensor.sensorID, new Sensor(sensor));
    });
    return result;
  }

  /**
   * Get NanoPrecise equipment access
   */
  getNanoEquipmentAccess(assetId: number) {
    return this.assetsApi.getNanoEquipmentAccess(assetId);
  }

  /**
   * Get NanoPrecise assets token
   */
  getNanoPreciseAssetsToken() {
    return this.assetsApi.getNanoPreciseAssetsToken();
  }

  // Cache for NanoPrecise asset details to prevent multiple API calls
  private _nanoPreciseCache = new Map<number, Promise<any[]>>();

  /**
   * Get NanoPrecise assets detail with caching
   */
  getNanoPreciseAssetsDetail(assetId: number): Promise<any[]> {
    // Check if we already have a pending or completed request for this assetId
    if (this._nanoPreciseCache.has(assetId)) {
      return this._nanoPreciseCache.get(assetId)!;
    }

    // Create new request and cache it
    const request = this.assetsApi.getNanoPreciseAssetsDetail(assetId);
    this._nanoPreciseCache.set(assetId, request);

    // Clear cache entry on error to allow retry
    request.catch(() => {
      this._nanoPreciseCache.delete(assetId);
    });

    return request;
  }

  /**
   * Clear NanoPrecise cache for a specific assetId or all
   */
  clearNanoPreciseCache(assetId?: number): void {
    if (assetId !== undefined) {
      this._nanoPreciseCache.delete(assetId);
    } else {
      this._nanoPreciseCache.clear();
    }
  }

  /**
   * Private: Replace sensor in both sensors and navigation arrays
   */
  private replaceSensor(equipmentID: number, sensorID: number, sensor: Sensor): void {
    this._sensors.update((sensors) => {
      const idx = sensors.findIndex((x) => x.sensorID === sensorID);
      if (idx >= 0) {
        sensors[idx] = sensor;
      }
      return [...sensors];
    });

    this._navigation.update((nav) => {
      const parent = nav.find((x) => x.objectID === equipmentID);
      if (parent && parent.sensors) {
        const parentIdx = parent.sensors.findIndex((x) => x.sensorID === sensorID);
        if (parentIdx >= 0) {
          parent.sensors[parentIdx] = sensor;
        }
      }
      return [...nav];
    });
  }

  /**
   * Private: Flatten plant hierarchy
   */
  private flattenPlant(plant: Location): Location[] {
    let locations: Location[] = [plant];
    let children = plant.children;

    while (children.length > 0) {
      locations = locations.concat(children);

      let grandchildren: Location[] = [];

      children.forEach((element) => {
        if (element.children.length > 0) {
          grandchildren = grandchildren.concat(element.children);
        }
      });

      children = grandchildren;
    }

    return locations;
  }

  // ==================== Equipment Monitor Specific Methods ====================
  async getAssetEM(
    id: number,
    disableCache = false,
    _measurementSystem?: MeasurementSystemSettings
  ): Promise<{
    asset: AssetDetail;
    plant: Location | undefined;
    breadcrumbsLoading: boolean;
  }> {
    // 1. Check cache
    if (!disableCache && this._assetDetailCache.has(id)) {
      const cached = this._assetDetailCache.get(id)!;
      if (Date.now() - cached.timestamp < this.ASSET_CACHE_TIME) {
        const plants = this.plants();
        const cachedAsset = cached.data as AssetDetail;
        return {
          asset: cachedAsset,
          plant: plants.find((plant) => plant.Assets.find((item) => item.objectID === id)),
          breadcrumbsLoading: false,
        };
      }
    }

    this._loading.set(true);

    try {
      // 2. Fetch asset from API
      const response = await this.assetsApi.getAssetDetail(id);
      const asset: any = AssetDetail.FromServiceResponse(response);

      // 3. Filter notifications: remove older duplicates, prefer critical over warning
      asset.notifications = (asset.notifications ?? []).filter(
        (item: any, _index: number, array: any[]) => {
          if (
            !item.onlyApplyWorseStatus &&
            array.some(
              (n: any) =>
                n.sensorID === item.sensorID &&
                n.measurementTypeID === item.measurementTypeID &&
                n.notificationID !== item.notificationID &&
                n.timestamp > item.timestamp
            )
          ) {
            return false;
          }
          if (item.isCritical) return true;
          const critical = array.find(
            (_item: any) =>
              _item.isCritical &&
              _item.objectID === item.objectID &&
              _item.sensorID === item.sensorID &&
              _item.measurementTypeID === item.measurementTypeID &&
              _item.timestamp === item.timestamp
          );
          return !critical;
        }
      );

      // 4. Resolve breadcrumbs by loading full navigation
      const regions = await this.loadNavigation(true);
      const allPlants = regions
        .map((region) => region.children)
        .flat(2)
        .map((country) => country.children)
        .flat(2);
      const allAssets: Asset[] = allPlants.map((plant: Location) => plant.Assets).flat(2);
      const matchedAsset: any = allAssets.find((a: Asset) => a.objectID === asset.objectID);

      if (matchedAsset && (!asset.breadcrumbs || asset.breadcrumbs.length === 0)) {
        asset.breadcrumbs = matchedAsset.breadcrumbs;
        asset.picture = matchedAsset.picture;
        asset.thumbnail = matchedAsset.thumbnail;
        asset.defaultImageId = matchedAsset.defaultImageId || '';
      }

      // 5. Status override based on breadcrumb-resolved asset
      const temp: any = new AssetDetail(asset, asset.breadcrumbs);
      if (temp.inactive && !asset.inactive) {
        asset.inactive = true;
        if (matchedAsset) matchedAsset.inactive = true;
        const navItem = this._navigation().find((n) => n.objectID === asset.objectID);
        if (navItem) navItem.inactive = true;
      } else if (temp.critical && !asset.critical) {
        asset.critical = true;
        if (matchedAsset) matchedAsset.critical = true;
        const navItem = this._navigation().find((n) => n.objectID === asset.objectID);
        if (navItem) navItem.critical = true;
      } else if (temp.warning && !asset.warning) {
        asset.warning = true;
        if (matchedAsset) matchedAsset.warning = true;
        const navItem = this._navigation().find((n) => n.objectID === asset.objectID);
        if (navItem) navItem.warning = true;
      }

      // 6. Merge temp into asset and cache
      const merged = Object.assign(temp, asset) as AssetDetail;

      this._assetDetailCache.set(id, {
        data: merged,
        timestamp: Date.now(),
      });
      this._currentAssetDetail.set(merged);

      // 7. Find parent plant
      const plant = allPlants.find((p) => p.Assets.find((item) => item.objectID === id));

      return {
        asset: merged,
        plant,
        breadcrumbsLoading: false,
      };
    } catch (error) {
      console.error('Failed to get asset detail (EM):', error);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Get asset detail with caching
   */
  async getAssetDetail(
    id: number,
    disableCache = false,
    _measurementSystem?: any
  ): Promise<{
    asset: any;
    plant: Location | undefined;
    breadcrumbsLoading: boolean;
  }> {
    // Check cache
    if (!disableCache && this._assetDetailCache.has(id)) {
      const cached = this._assetDetailCache.get(id)!;
      if (Date.now() - cached.timestamp < this.ASSET_CACHE_TIME) {
        const plants = this.plants();
        return {
          asset: cached.data,
          plant: plants.find((plant) => plant.Assets.find((item) => item.objectID === id)),
          breadcrumbsLoading: false,
        };
      }
    }

    this._loading.set(true);

    try {
      const response = await this.assetsApi.getAssetDetail(id);

      // Process response - adapt to your AssetDetail model
      const asset = response; // You may need to process this through AssetDetail.FromServiceResponse()

      // Cache the result
      this._assetDetailCache.set(id, {
        data: asset,
        timestamp: Date.now(),
      });

      this._currentAssetDetail.set(asset);

      // Find the plant
      const plants = this.plants();
      const plant = plants.find((p) => p.Assets.find((item) => item.objectID === id));

      return {
        asset,
        plant,
        breadcrumbsLoading: false,
      };
    } catch (error) {
      console.error('Failed to get asset detail:', error);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  async getSensorsEM(
    id: number,
    _disableCache = false,
    _measurementSystem?: MeasurementSystemSettings
  ): Promise<{
    sensors: EMSensor[];
    health: EMCurrentTelemetry[];
    steamtrap: EMCurrentTelemetry[];
    temperature: EMCurrentTelemetry | undefined;
  }> {
    this._loading.set(true);

    try {
      const response = await this.assetsApi.getSensorsWithTelemetry(id);
      const rawTelemetry = response.telemetry ?? [];

      // 1. Parse into typed buckets
      const sensors = ParseSensorsFromRawSensorTelemetry(rawTelemetry);
      const health = ParseHealthEM(rawTelemetry);
      const steamtrap = ParseSteamTrapFromRawSensorTelemetry(rawTelemetry);
      const temperature = ParseTemperatureFromRawSensorTelemetry(rawTelemetry);

      // 2. Cross-reference steamtrap status onto matching sensor telemetry
      steamtrap.forEach((item: any) => {
        const sensor: any = sensors.find(
          (_sensor: any) => _sensor.sensorID === item.sensor?.sensorID
        );
        if (sensor?.telemetry) {
          sensor.telemetry.forEach((telemetry: any) => {
            telemetry.critical = item.critical;
            telemetry.inactive = item.telemetry;
            telemetry.warning = item.warning;
            telemetry.inactive = item.inactive;
          });
        }
      });

      const result = { sensors, health, steamtrap, temperature };

      // 3. Update signal
      this._sensorsWithTelemetry.set({
        sensors: result.sensors as any[],
        health: result.health as any[],
        steamtrap: result.steamtrap as any[],
        temperature: result.temperature,
      });

      return result;
    } catch (error) {
      console.error('Failed to get sensors with telemetry (EM):', error);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Get sensors with telemetry data
   */
  async getSensorsWithTelemetry(id: number): Promise<{
    sensors: any[];
    health: any[];
    steamtrap: any[];
    temperature: any;
  }> {
    this._loading.set(true);

    try {
      const response = await this.assetsApi.getSensorsWithTelemetry(id);

      // Return the raw telemetry data - parsing should be done by the caller
      // or you can add parsing methods here
      const result = {
        sensors: response.telemetry || [],
        health: [],
        steamtrap: [],
        temperature: null,
      };

      // Update the signal with the latest data
      this._sensorsWithTelemetry.set(result);

      return result;
    } catch (error) {
      console.error('Failed to get sensors with telemetry:', error);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  async loadSensorsWithTelemetry(
    id: number,
    disableCache = false,
    measurementSystem?: any
  ): Promise<{
    sensors: any[];
    health: any[];
    steamtrap: any[];
    temperature: any;
  }> {
    return this.getSensorsEM(id, disableCache, measurementSystem);
  }

  /**
   * Get notifications
   */
  async getNotifications(): Promise<any[]> {
    this._loading.set(true);

    try {
      // If navigation is being loaded by getRegionsEM, wait for it to complete
      // so breadcrumbs can be resolved properly
      if (this._navigation().length === 0 && this._navLoadPromise) {
        await this._navLoadPromise;
      }
      const response = await this.assetsApi.getNotifications();
      const raw = response.notifications || [];

      // Use whatever navigation is currently available for breadcrumb resolution
      const nav = this._navigation();

      // Resolve breadcrumbs from navigation data
      const withBreadcrumbs = raw.map((n: any) => new Notification(n, nav as any[]));

      // Deduplicate: remove older notifications for the same sensor+measurement
      const notifications = withBreadcrumbs.filter((item: any, _index: number, array: any[]) => {
        if (
          !item.onlyApplyWorseStatus &&
          array.filter(
            (n: any) =>
              n.sensorID === item.sensorID &&
              n.measurementTypeID === item.measurementTypeID &&
              n.notificationID !== item.notificationID &&
              n.timestamp > item.timestamp
          ).length > 0
        ) {
          return false;
        }
        // Keep critical; filter out non-critical when critical exists for same object+sensor+measurement+timestamp
        if (item.isCritical) return true;
        const critical = array.find(
          (_item: any) =>
            _item.isCritical &&
            _item.objectID === item.objectID &&
            _item.sensorID === item.sensorID &&
            _item.measurementTypeID === item.measurementTypeID &&
            _item.timestamp === item.timestamp
        );
        return !critical;
      });

      this._notifications.set(notifications);
      return notifications;
    } catch (error: any) {
      console.error('Failed to get notifications:', error);

      // Re-throw authentication errors to allow error interceptor to handle redirect
      if (error?.status === 401 || error?.statusCode === 401) {
        throw error;
      }

      return [];
    } finally {
      this._loading.set(false);
    }
  }

  async loadNotificationsWithFilter(): Promise<{
    notifications: Notification[];
    filtered: boolean;
  }> {
    const navigationAlreadyLoaded = this._navigation().length > 0;

    await this.getNotifications();

    if (this._navigation().length === 0) {
      await this.loadNavigation();
    } else if (navigationAlreadyLoaded) {
      await this.loadRegions();
    }

    return this.filteredNotifications();
  }

  private filterNotifications(
    notifications: Notification[],
    navigation: IOTObject[],
    searchTerm: string,
    filter: LocationFilter
  ): Notification[] {
    // Create LocationFilter without status filtering
    const assetFilter = new LocationFilter(filter);
    assetFilter.inactive = false;
    assetFilter.working = false;
    assetFilter.warning = false;
    assetFilter.critical = false;

    // Determine assets which would pass the current filtering options (-status filter)
    const assets: Asset[] = navigation
      .filter((item) => item.layerID === 1) // filter for regions
      .map((item: IOTObject) => new Location(item, navigation))
      .map((item: Location) => item.ApplySearch(searchTerm))
      .filter((item): item is Location => item !== null)
      .map((item: Location) => item.ApplyFilter(assetFilter))
      .filter((item): item is Location => item !== null)
      .map((item: Location) => item.Assets)
      .flat(2);

    // Return only those notifications which belong to the previously filtered assets
    let filteredNotifs = notifications
      .map((notification: Notification) => {
        const asset = assets.find((_asset: Asset) => _asset.objectID === notification.objectID);
        if (!asset) return null;
        notification.breadcrumbs = asset.breadcrumbs;
        return notification;
      })
      .filter((item): item is Notification => item !== null)
      .filter((item) =>
        item.equipmentType === 'Rotating Equipment' ? true : item.notificationStatus !== 'repaired'
      );

    // Apply status filtering if any status filter is enabled
    if (filter.critical || filter.warning || filter.inactive || filter.working) {
      filteredNotifs = filteredNotifs.filter(
        (notification: Notification) =>
          (filter.critical && notification.isCritical && !notification.inactive) ||
          (filter.warning && notification.isWarning) ||
          (filter.inactive && notification.inactive) ||
          (filter.working &&
            !notification.isCritical &&
            !notification.isWarning &&
            !notification.inactive)
      );
    }

    // Sort by timestamp descending
    return filteredNotifs.sort((a, b) =>
      a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : 0
    );
  }

  /**
   * Get object history
   */
  async getObjectHistory(objectId: number, sensorId: number, topN = 1000): Promise<any[]> {
    try {
      const response = await this.assetsApi.getObjectHistory(objectId, sensorId, topN);
      if (this.cfg.appName === 'em') {
        return (
          response.items
            .map((item: any) => Object.assign(new HistoryItem(), item))
            .sort((a: HistoryItem, b: HistoryItem) => b.timestamp.localeCompare(a.timestamp)) || []
        );
      } else {
        return response.items || [];
      }
    } catch (error) {
      console.error('Failed to get object history:', error);
      return [];
    }
  }

  /**
   * Get telemetry data
   */
  async getTelemetry(
    objectID: number,
    sensorID: number,
    measurementID: number,
    _measurementSystem: MeasurementSystemSettings,
    timeframe?: {
      start: Date;
      end: Date;
      custom?: boolean;
    }
  ): Promise<TelemetryHistory> {
    this._loading.set(true);

    try {
      if (!timeframe) {
        timeframe = {
          start: new Date(new Date().setDate(new Date().getDate() - 1)),
          end: new Date(),
          custom: false,
        };
      }

      const end = new Date(
        new Date(timeframe.end).setHours(new Date(timeframe.end).getHours() + 25)
      );

      const response = await this.assetsApi.getTelemetry(
        objectID,
        sensorID,
        measurementID,
        timeframe.start.toISOString(),
        end.toISOString()
      );

      const telemetry = new TelemetryHistory();
      telemetry.metadata = (response.metadata || []).map((metadata: any) => {
        return {
          ...metadata,
          timeframe: timeframe,
        };
      });
      telemetry.telemetry = response.telemetry || [];
      telemetry.thresholds = response.thresholds || [];

      // Apply measurement system conversion
      //telemetry.ApplyMeasurementSystem(measurementSystem);

      // Store in signal for reactive access
      this._telemetryData.set(telemetry);

      return telemetry;
    } catch (error) {
      console.error('Failed to get telemetry:', error);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Get MapBox keys with caching
   */
  async getMapBoxKeys(): Promise<{
    satelliteView: string;
    mapView: string;
    geoCodingToken: string;
  }> {
    const cached = this._mapboxKeys();
    if (cached) {
      return cached;
    }

    try {
      const keys = await this.assetsApi.getMapBoxKeys();
      this._mapboxKeys.set(keys);
      return keys;
    } catch (error) {
      console.error('Failed to get MapBox keys:', error);
      throw error;
    }
  }

  /**
   * Get NanoPrecise refresh token
   */
  async getNanoPreciseRefreshToken(assetId: number): Promise<any> {
    try {
      return await this.assetsApi.getNanoPreciseRefreshToken(assetId);
    } catch (error) {
      console.error('Failed to get NanoPrecise refresh token:', error);
      throw error;
    }
  }

  /**
   * Get NanoPrecise dashboard parameters for SSO
   */
  async goToNanoPreciseDashboard(assetId: number): Promise<any> {
    try {
      return await this.assetsApi.goToNanoPreciseDashboard(assetId);
    } catch (error) {
      console.error('Failed to get NanoPrecise dashboard params:', error);
      throw error;
    }
  }

  /**
   * Get NanoPrecise asset health status
   */
  async getNanoPreciseAssetsHealthStatus(assetId: number): Promise<any[]> {
    try {
      return await this.assetsApi.getNanoPreciseAssetsHealthStatus(assetId);
    } catch (error) {
      console.error('Failed to get NanoPrecise health status:', error);
      return [];
    }
  }

  /**
   * Get sensor subscription details
   */
  async getSensorSubscriptionDetails(sensorId: number): Promise<any> {
    try {
      return await this.assetsApi.getSensorSubscriptionDetails(sensorId);
    } catch (error) {
      console.error('Failed to get sensor subscription details:', error);
      throw error;
    }
  }

  /**
   * Get customer sensor subscription details
   */
  async getCustomerSensorSubscriptionDetails(objectID: number): Promise<any> {
    try {
      return await this.assetsApi.getCustomerSensorSubscriptionDetails(objectID);
    } catch (error) {
      console.error('Failed to get sensor subscription details:', error);
      throw error;
    }
  }

  async getRegionsEM(forceReload = false): Promise<Location[]> {
    Location.emMode = true;
    AssetDetail.emMode = true;

    if (this._navInProgress()) {
      return this._regions();
    }

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    const sortFn = (a: IOTObject, b: IOTObject) => {
      if (a.isAsset !== b.isAsset) return a.isAsset ? 1 : -1;
      return collator.compare(a.objectName, b.objectName);
    };

    if (this._navigation().length === 0 || forceReload) {
      this._navInProgress.set(true);
      this._loading.set(false);

      try {
        // Load notifications before nav so status overrides can be applied
        if (this.cfg.appName === 'em') {
          await this.getNotifications();
        }

        // Store a promise for the nav-loading portion so other callers
        // (e.g. getNotifications from the notifications page) can await it
        // instead of making a duplicate nav/ call
        this._navLoadPromise = this.assetsApi.getNavigation(true).then((response) => {
          const isArrayResponse = Array.isArray(response);
          const items = isArrayResponse ? response : response.items || [];
          const storageOptions = isArrayResponse ? null : response.storageOptions;

          this._storageOptions.set(storageOptions);

          const deduped = this.removeDuplicateRegions(items);

          const currentNotifications = this._notifications();
          const withStatus =
            currentNotifications.length > 0
              ? this.overrideObjectStatusByNotifications(deduped, currentNotifications)
              : deduped;

          const sorted = withStatus.sort(sortFn);
          this._navigation.set(sorted);

          // Re-resolve notification breadcrumbs now that navigation is loaded
          const rawNotifications = this._notifications();
          if (rawNotifications.length > 0) {
            const withBreadcrumbs = rawNotifications.map(
              (n: any) => new Notification(n, sorted as any[])
            );
            this._notifications.set(withBreadcrumbs);
          }

          this.matchSensorsToAssets();

          const regionItems = sorted.filter((item) => item.layerID === 1);
          const regions = regionItems
            .sort(sortFn)
            .map((item: IOTObject) => new Location(item, sorted))
            .filter((item) => !!item);

          this._regions.set(regions);
        });

        await this._navLoadPromise;

        return this._regions();
      } catch (error: any) {
        console.error('Failed to load navigation (EM):', error);
        this._regions.set([]);

        if (error?.status === 401 || error?.statusCode === 401) {
          throw error;
        }

        return [];
      } finally {
        this._navInProgress.set(false);
        this._loading.set(false);
        this._navLoadPromise = null;
      }
    }

    // Return cached data with notification status override re-applied
    const currentNotifications = this._notifications();
    const navData = this._navigation();
    const withStatus =
      currentNotifications.length > 0
        ? this.overrideObjectStatusByNotifications(navData, currentNotifications)
        : navData;

    const regionObjects = withStatus
      .filter((item: IOTObject) => item.layerID === 1)
      .sort(sortFn)
      .map((item: IOTObject) => new Location(item, withStatus))
      .filter((item: Location) => !!item);

    this._regions.set(regionObjects);
    return regionObjects;
  }

  /**
   * Load navigation for equipment monitor (with storage options)
   * @deprecated Use getRegionsEM() instead
   */
  async loadNavigationEM(forceReload = false): Promise<Location[]> {
    return this.getRegionsEM(forceReload);
  }

  /**
   * Find location by ID in navigation tree
   */
  findLocation(id: number): Location | undefined {
    const regions = this._regions().filter((item) => !!item);

    for (const region of regions) {
      // Use the find method from Location class
      const found = region.find(id);
      if (found && found.objectID === id) {
        // Convert OTObject to Location if needed
        if (found instanceof Location) {
          return found;
        }
        // If it's an OTObject, we need to find the actual Location wrapper
        return this.findLocationInTree(region, id);
      }
    }

    return undefined;
  }

  /**
   * Private helper to recursively find Location in tree
   */
  private findLocationInTree(location: Location, id: number): Location | undefined {
    if (location.objectID === id) {
      return location;
    }

    for (const child of location.children) {
      const found = this.findLocationInTree(child, id);
      if (found) {
        return found;
      }
    }

    return undefined;
  }
}
